import { Fragment, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Check, CheckSquare, ChevronDown, ChevronLeft, Clock, Download, Filter, MoreVertical, Pencil, Square, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';
import { imagesApi } from '../../services/annotatorApi';
import { colorForClassIndex } from '../../utils/classColors';
import type { Class as ClassModel, ImageMeta, ImageStatus, Split } from '../../types/api';
import { MenuDropdown } from '../ui';
import SplitChip from '../SplitChip';

/**
 * ImageGridSidebar — left sidebar with status filter + image thumbnail
 * list. Styling mirrors the right rail's view picker (RightRail.tsx) so
 * both dropdowns feel like the same component:
 *   • Same Listbox.Button shape (rounded-md, border, text-[13px], hover)
 *   • Same Listbox.Options panel (rounded-md, shadow-xl, primary-* tinted
 *     active row, check mark on selected)
 *   • isDarkMode prop instead of class-based dark: variants (matches the
 *     codebase-wide convention)
 *   • primary-* (IGNODE sky-blue) accents replacing the old rose-* tokens
 */
interface ImageGridSidebarProps {
    isDarkMode?: boolean;
    images: ImageMeta[];
    classes: ClassModel[];
    activeImageId: string | null;
    statusFilter: ImageStatus | '';
    /** Currently-applied class filter — null when filtering by status or
     *  showing all. Mutually exclusive with statusFilter. */
    classFilter: number | null;
    orgId: string | undefined;
    onSelectImage: (id: string) => void;
    onChangeStatusFilter: (status: ImageStatus | '') => void;
    /** Opens the class picker modal. Sidebar doesn't own the modal —
     *  Annotator does, so it can pass `classes` + `activeImageBoxes`. */
    onOpenClassFilter: () => void;
    /** Clears the class filter (used by the "All" option). */
    onClearClassFilter: () => void;
    /** Open the per-image download modal (annotated → modal asking raw vs.
     *  annotated; pending images skip the modal and download raw direct). */
    onDownloadImage: (img: ImageMeta) => void;
    /** Open the per-image delete confirm modal. */
    onDeleteImage: (img: ImageMeta) => void;
    /** Trigger a streaming bulk-ZIP download of every original image. */
    onDownloadAllImages?: () => void;
    /** D.14 — bulk delete N selected images. Parent owns the confirm modal +
     *  the actual API call (imagesApi.bulkRemove); sidebar just emits the
     *  selected IDs and clears selection optimistically when the parent's
     *  state update flows back in. */
    onBulkDelete?: (ids: string[]) => void;
    /** When provided, a close-chevron renders in the top toolbar (only
     *  visible <lg). Lets the drawer be collapsed inline rather than
     *  via the backdrop tap. */
    onCollapse?: () => void;
    // G.6 (2026-06-07 UX revision): the inline split-summary banner
    // moved to the top "Project is locked" strip in the canvas area,
    // with a modal for full details. Per-row train/val/test chips
    // (rendered below from ImageMeta.split) stay — they're small and
    // don't eat list space.
    /** G.6 — split filter dropdown surface. Only meaningful on a
     *  locked project; pass null/'' on unlocked projects. */
    splitFilter?: Split | '';
    onChangeSplitFilter?: (split: Split | '') => void;
    /** When true, the filter dropdown shows train/val/test entries
     *  above the class-picker action. Caller passes project.is_locked. */
    isLocked?: boolean;
}

export default function ImageGridSidebar({
    isDarkMode = false,
    images,
    classes,
    activeImageId,
    statusFilter,
    classFilter,
    orgId,
    onSelectImage,
    onChangeStatusFilter,
    onOpenClassFilter,
    onClearClassFilter,
    onDownloadImage,
    onDeleteImage,
    onDownloadAllImages,
    onBulkDelete,
    onCollapse,
    splitFilter,
    onChangeSplitFilter,
    isLocked = false,
}: ImageGridSidebarProps) {
    const { t } = useTranslation('canvas');
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    // D.14 — edit mode toggle + selection set. Selection survives the
    // status filter changing (we just hide the rows that fall out of the
    // current filter), but exiting edit mode clears it.
    const [editMode, setEditMode] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(() => new Set());

    // Prune selection when images list changes (e.g. after a successful
    // bulk delete — parent removed the rows, and the IDs the user picked
    // no longer exist).
    useEffect(() => {
        setSelected((prev) => {
            const visible = new Set(images.map((i) => i.id));
            let drift = false;
            const next = new Set<string>();
            prev.forEach((id) => {
                if (visible.has(id)) next.add(id);
                else drift = true;
            });
            return drift ? next : prev;
        });
    }, [images]);

    function toggleEditMode() {
        setEditMode((prev) => {
            if (prev) setSelected(new Set()); // exiting → clear selection
            return !prev;
        });
    }
    function toggleSelected(id: string) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }
    function handleBulkDelete() {
        if (!onBulkDelete || selected.size === 0) return;
        onBulkDelete(Array.from(selected));
    }
    // D.14 — "Select all" toggles between selecting every CURRENTLY VISIBLE
    // image (respects active filter) and clearing the selection. When
    // every visible image is already selected, clicking acts as "Clear".
    const allVisibleSelected = images.length > 0 && images.every((i) => selected.has(i.id));
    function toggleSelectAll() {
        if (allVisibleSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(images.map((i) => i.id)));
        }
    }

    // Status options — 'flagged' lives in the schema + the ImageStatus type
    // but the feature was never wired (no flag/unflag UI shipped). Hidden
    // from the dropdown to avoid an option that always returns 0 results.
    //
    // Sentinel `__class_picker__` is a synthetic action option: picking it
    // doesn't set a status, it opens the ClassPickerModal. The dropdown's
    // selection chip is then driven by classFilter (not statusFilter) and
    // reads "Class: <name>". One dropdown, two filter dimensions, mutually
    // exclusive.
    //
    // Icon tints follow the Agent Builder palette so each row reads as a
    // distinct status chip at a glance: All neutral, Pending amber-ish
    // (work in progress), Annotated emerald (done), Class picker primary.
    type FilterOption = {
        value: ImageStatus | '' | '__class_picker__' | '__split_train__' | '__split_val__' | '__split_test__';
        label: string;
        count?: number;
        isAction?: boolean;
        /** Header section labels — rendered non-interactive between
         *  groups of options. Mirrors the MenuDropdown section header
         *  pattern. */
        isSectionLabel?: boolean;
    };
    const filterIconCls = (v: FilterOption['value']): string => {
        if (v === 'pending') return isDarkMode ? 'text-amber-400' : 'text-amber-500';
        if (v === 'annotated') return isDarkMode ? 'text-emerald-400' : 'text-emerald-500';
        return isDarkMode ? 'text-primary-400' : 'text-primary-600';
    };
    const options: FilterOption[] = [
        { value: '', label: t('status_all'), count: images.length },
        { value: 'pending', label: t('status_pending'), count: images.filter((i) => i.status === 'pending').length },
        { value: 'annotated', label: t('status_annotated'), count: images.filter((i) => i.status === 'annotated').length },
    ];
    // G.6 — split filter section. Only meaningful when project is
    // locked (otherwise image.split is null for every image). Counts
    // reflect the underlying `images` (pre-other-filter) so customers
    // see the full project distribution, not a recursive subset.
    if (isLocked && onChangeSplitFilter) {
        options.push({
            value: '__split_train__',
            label: t('filter_split_train', { defaultValue: 'Training only' }),
            count: images.filter((i) => i.split === 'train').length,
        });
        options.push({
            value: '__split_val__',
            label: t('filter_split_val', { defaultValue: 'Validation only' }),
            count: images.filter((i) => i.split === 'val').length,
        });
        options.push({
            value: '__split_test__',
            label: t('filter_split_test', { defaultValue: 'Testing only' }),
            count: images.filter((i) => i.split === 'test').length,
        });
    }
    options.push({ value: '__class_picker__', label: t('filter_by_class', { defaultValue: 'Filter by class…' }), isAction: true });

    const activeClass = classFilter !== null
        ? classes.find((c) => c.class_index === classFilter) ?? null
        : null;
    const activeClassColor = activeClass
        ? (activeClass.color || colorForClassIndex(activeClass.class_index))
        : null;

    // G.6 — current split filter (if any). Translated to its menu value
    // for the listbox selection state.
    const activeSplitValue: FilterOption['value'] | null =
        splitFilter === 'train' ? '__split_train__'
        : splitFilter === 'val' ? '__split_val__'
        : splitFilter === 'test' ? '__split_test__'
        : null;

    // What the Listbox.Button renders. Priority: class filter →
    // split filter → status filter. Status defaults to "All".
    const currentLabel = activeClass
        ? t('filter_class_active', { defaultValue: 'Filter: {{name}}', name: activeClass.name })
        : activeSplitValue !== null
            ? (options.find((o) => o.value === activeSplitValue) ?? options[0]).label
            : (options.find((o) => !o.isAction && o.value === statusFilter) ?? options[0]).label;
    const currentCount = activeClass
        ? images.length
        : activeSplitValue !== null
            ? (options.find((o) => o.value === activeSplitValue) ?? options[0]).count
            : (options.find((o) => !o.isAction && o.value === statusFilter) ?? options[0]).count;
    const currentIsClass = !!activeClass;

    function handlePick(value: FilterOption['value']) {
        if (value === '__class_picker__') {
            onOpenClassFilter();
            return;
        }
        // G.6 — split filter pick. Mutually-exclusive with class filter
        // for now (status + split can coexist).
        if (value === '__split_train__' || value === '__split_val__' || value === '__split_test__') {
            if (classFilter !== null) onClearClassFilter();
            const split: Split = value === '__split_train__' ? 'train'
                : value === '__split_val__' ? 'val'
                : 'test';
            onChangeSplitFilter?.(split);
            return;
        }
        // Status pick → clear class + split filters (status is the
        // canonical "show everything" reset).
        if (classFilter !== null) onClearClassFilter();
        if (splitFilter && onChangeSplitFilter) onChangeSplitFilter('');
        onChangeStatusFilter(value as ImageStatus | '');
    }

    const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

    return (
        <>
            {/* One-row header: filter dropdown on the LEFT (flex-1), action
                icons (download all / select-all / edit / bulk-delete) on
                the RIGHT. Saves vertical space, especially on small
                screens where the canvas is the priority. */}
            <div
                className={cn(
                    'px-3 py-2 border-b flex items-center gap-2',
                    isDarkMode ? 'border-gray-700' : 'border-gray-200',
                )}
            >
                <Listbox
                    value={
                        currentIsClass
                            ? '__class_picker__'
                            : activeSplitValue !== null
                                ? activeSplitValue
                                : statusFilter
                    }
                    onChange={handlePick}
                >
                    <div className="relative flex-1 min-w-0">
                        <Listbox.Button
                            className={cn(
                                'w-full inline-flex items-center gap-2 px-3 py-2 rounded-md border text-[13px] font-semibold transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700'
                                    : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50',
                            )}
                        >
                            {activeClassColor ? (
                                <span
                                    className="h-3 w-3 rounded-full shrink-0"
                                    style={{ backgroundColor: activeClassColor }}
                                    aria-hidden="true"
                                />
                            ) : (
                                <Filter className={cn('h-3.5 w-3.5 shrink-0', filterIconCls(statusFilter))} />
                            )}
                            <span className="flex-1 text-left truncate">{currentLabel}</span>
                            <span
                                className={cn(
                                    'text-[10px] tabular-nums px-1.5 rounded-full',
                                    isDarkMode ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-50 text-primary-700',
                                )}
                            >
                                {currentCount}
                            </span>
                            <ChevronDown className={cn('h-3.5 w-3.5 shrink-0', muted)} />
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Listbox.Options
                                className={cn(
                                    'absolute left-0 right-0 mt-1 z-50 rounded-md border shadow-xl overflow-hidden py-1 text-[13px]',
                                    isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
                                )}
                            >
                                {options.map((opt, idx) => {
                                    const isDivider = opt.isAction && idx > 0;
                                    return (
                                        <div key={opt.value}>
                                            {isDivider && (
                                                <div className={cn('my-1 border-t', isDarkMode ? 'border-gray-700' : 'border-gray-200')} />
                                            )}
                                            <Listbox.Option
                                                value={opt.value}
                                                className={({ active, selected }) =>
                                                    cn(
                                                        'px-3 py-2 flex items-center gap-2 cursor-pointer',
                                                        active ? (isDarkMode ? 'bg-gray-800' : 'bg-gray-50') : (isDarkMode ? 'text-gray-300' : 'text-gray-700'),
                                                        // Highlight Class picker row when a class filter is currently active.
                                                        selected && (isDarkMode ? 'text-primary-300' : 'text-primary-700'),
                                                        opt.isAction && (isDarkMode ? 'text-primary-300' : 'text-primary-600'),
                                                    )
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span className="flex-1 truncate">{opt.label}</span>
                                                        {opt.count !== undefined && (
                                                            <span className={cn('text-[10px] tabular-nums', muted)}>{opt.count}</span>
                                                        )}
                                                        {selected && !opt.isAction && (
                                                            <Check className={cn('h-3.5 w-3.5', isDarkMode ? 'text-primary-300' : 'text-primary-600')} />
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        </div>
                                    );
                                })}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </Listbox>

                {/* Action icons — right side of the same row.
                    Order: Download all → Select-all (edit only) → Bulk-delete
                    (edit only) → Edit/Cancel toggle. Counter chip ("{n}
                    selected") moves to its own micro-row beneath when in
                    edit mode so it doesn't crowd the row at narrow widths. */}
                {images.length > 0 && (
                <div
                    className={cn(
                        'flex items-center gap-1.5 shrink-0',
                    )}
                >
                    {/* G.7 UX revision (2026-06-07) — out of edit mode,
                        Download All + Select Images collapse into a
                        single 3-dot "more" menu so the header row stays
                        compact (especially at narrow widths). In edit
                        mode the Cancel + Select-all + Bulk-delete icons
                        below stay inline because they're contextual to
                        the active selection. */}
                    {!editMode && (onDownloadAllImages || onBulkDelete) && (
                        <MenuDropdown
                            isDarkMode={isDarkMode}
                            trigger={
                                <button
                                    type="button"
                                    title={t('image_list_actions', { defaultValue: 'More actions' })}
                                    aria-label={t('image_list_actions', { defaultValue: 'More actions' })}
                                    className={cn(
                                        // Bare icon — no border, no background.
                                        // Matches the chevron-collapse next to it.
                                        'inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors',
                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                        isDarkMode
                                            ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
                                    )}
                                >
                                    <MoreVertical className="h-4 w-4" />
                                </button>
                            }
                            sections={[
                                {
                                    label: t('image_list_actions_section', { defaultValue: 'Image list' }),
                                    items: [
                                        ...(onDownloadAllImages
                                            ? [{
                                                key: 'download-all',
                                                icon: Download,
                                                iconClassName: isDarkMode ? 'text-primary-300' : 'text-primary-600',
                                                label: t('download_all_images_count', {
                                                    defaultValue: 'Download all ({{count}} images)',
                                                    count: images.length,
                                                }),
                                                onClick: onDownloadAllImages,
                                            }]
                                            : []),
                                        ...(onBulkDelete
                                            ? [{
                                                key: 'select-images',
                                                icon: Pencil,
                                                iconClassName: isDarkMode ? 'text-gray-300' : 'text-gray-600',
                                                label: t('edit_images', { defaultValue: 'Select images' }),
                                                onClick: toggleEditMode,
                                            }]
                                            : []),
                                    ],
                                },
                            ]}
                        />
                    )}
                    {editMode && (
                        <button
                            type="button"
                            onClick={toggleEditMode}
                            title={t('cancel_edit_mode', { defaultValue: 'Cancel multi-select' })}
                            aria-label={t('cancel_edit_mode', { defaultValue: 'Cancel multi-select' })}
                            aria-pressed={true}
                            className={cn(
                                'inline-flex items-center justify-center h-8 w-8 rounded-md border transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                isDarkMode
                                    ? 'bg-primary-500/15 border-primary-500/40 text-primary-300'
                                    : 'bg-primary-50 border-primary-300 text-primary-700',
                            )}
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                    {editMode && onBulkDelete && (
                        <button
                            type="button"
                            onClick={toggleSelectAll}
                            title={allVisibleSelected
                                ? t('clear_selection', { defaultValue: 'Clear selection' })
                                : t('select_all', { defaultValue: 'Select all visible' })}
                            aria-label={allVisibleSelected
                                ? t('clear_selection', { defaultValue: 'Clear selection' })
                                : t('select_all', { defaultValue: 'Select all visible' })}
                            aria-pressed={allVisibleSelected}
                            className={cn(
                                'inline-flex items-center justify-center h-8 w-8 rounded-md border transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                allVisibleSelected
                                    ? isDarkMode
                                        ? 'bg-primary-500/15 border-primary-500/40 text-primary-300'
                                        : 'bg-primary-50 border-primary-300 text-primary-700'
                                    : isDarkMode
                                        ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
                            )}
                        >
                            {allVisibleSelected
                                ? <CheckSquare className="h-3.5 w-3.5" />
                                : <Square className="h-3.5 w-3.5" />}
                        </button>
                    )}
                    {editMode && onBulkDelete && (
                        <button
                            type="button"
                            onClick={handleBulkDelete}
                            disabled={selected.size === 0}
                            title={selected.size > 0
                                ? t('delete_n_selected', {
                                    defaultValue: 'Delete {{count}} selected',
                                    count: selected.size,
                                })
                                : t('select_to_delete', {
                                    defaultValue: 'Select images to delete',
                                })}
                            aria-label={t('delete_n_selected', {
                                defaultValue: 'Delete {{count}} selected',
                                count: selected.size,
                            })}
                            className={cn(
                                'inline-flex items-center justify-center h-8 w-8 rounded-md border transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500',
                                selected.size === 0
                                    ? isDarkMode
                                        ? 'bg-gray-800/50 border-gray-700/60 text-gray-600 cursor-not-allowed'
                                        : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode
                                        ? 'bg-red-900/30 border-red-800/60 text-red-300 hover:bg-red-900/50'
                                        : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100',
                            )}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                )}
                {/* Close chevron — only <lg. Sits at the far right of the
                    single-row top toolbar so the rail can be collapsed
                    without reaching for the screen edge. */}
                {onCollapse && (
                    <button
                        type="button"
                        onClick={onCollapse}
                        aria-label={t('close_left_rail', { defaultValue: 'Close image list' })}
                        title={t('close_left_rail', { defaultValue: 'Close image list' })}
                        className={cn(
                            'p-1.5 rounded-md transition-colors shrink-0',
                            isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500',
                        )}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Selection counter — own row beneath the toolbar so narrow
                viewports never need to choose between the count chip and
                the action icons. Only rendered in edit mode. */}
            {editMode && images.length > 0 && (
                <div
                    className={cn(
                        'px-3 py-1 border-b text-[11px] tabular-nums',
                        isDarkMode ? 'border-gray-700 text-gray-400 bg-gray-900/30' : 'border-gray-200 text-gray-500 bg-gray-50',
                    )}
                >
                    {t('n_selected', {
                        defaultValue: '{{count}} selected',
                        count: selected.size,
                    })}
                </div>
            )}

            {/* Image list */}
            <ul className="flex-1 overflow-y-auto p-2 space-y-1">
                {images.length === 0 && (
                    <li className={cn('text-sm py-4 text-center italic', muted)}>
                        {t('no_images_in_sidebar', { defaultValue: 'No images yet — upload from the header.' })}
                    </li>
                )}
                {images.map((img) => {
                    const active = img.id === activeImageId;
                    const StatusIcon = img.status === 'annotated' ? Check : Clock;
                    const statusColor = img.status === 'annotated'
                        ? 'text-emerald-500'
                        : isDarkMode ? 'text-gray-500' : 'text-gray-400';
                    const isHovered = hoveredId === img.id;
                    const isSelected = selected.has(img.id);
                    return (
                        <li
                            key={img.id}
                            onMouseEnter={() => setHoveredId(img.id)}
                            onMouseLeave={() => setHoveredId(null)}
                        >
                            <div
                                onClick={() => {
                                    // In edit mode the whole row toggles
                                    // selection (matches Gmail/Photos
                                    // intuition). Out of edit mode it
                                    // navigates as before.
                                    if (editMode) toggleSelected(img.id);
                                    else onSelectImage(img.id);
                                }}
                                className={cn(
                                    'group w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors duration-150 cursor-pointer',
                                    editMode && isSelected
                                        ? isDarkMode
                                            ? 'bg-primary-500/20 border-l-2 border-primary-400'
                                            : 'bg-primary-100 border-l-2 border-primary-500'
                                        : active && !editMode
                                            ? isDarkMode
                                                ? 'bg-primary-500/15 border-l-2 border-primary-400'
                                                : 'bg-primary-50 border-l-2 border-primary-500'
                                            : cn(
                                                'border-l-2 border-transparent',
                                                isDarkMode ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50',
                                            ),
                                )}
                            >
                                {editMode && (
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleSelected(img.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={t('select_image', {
                                            defaultValue: 'Select {{name}}',
                                            name: img.filename,
                                        })}
                                        className={cn(
                                            'h-4 w-4 rounded shrink-0 cursor-pointer',
                                            'text-primary-600 focus:ring-primary-500',
                                            isDarkMode
                                                ? 'bg-gray-800 border-gray-600'
                                                : 'bg-white border-gray-300',
                                        )}
                                    />
                                )}
                                <img
                                    src={imagesApi.thumbnailUrl(img.id, orgId)}
                                    alt=""
                                    className={cn(
                                        'h-10 w-10 object-cover rounded shrink-0',
                                        isDarkMode ? 'bg-gray-800' : 'bg-gray-200',
                                    )}
                                    loading="lazy"
                                />
                                <StatusIcon className={cn('h-3.5 w-3.5 shrink-0', statusColor)} />
                                <span
                                    className={cn(
                                        'flex-1 text-[13px] truncate',
                                        active && !editMode
                                            ? isDarkMode ? 'text-gray-100 font-semibold' : 'text-gray-900 font-semibold'
                                            : isDarkMode ? 'text-gray-300' : 'text-gray-700',
                                    )}
                                >
                                    {img.filename}
                                </span>
                                {/* G.6 — uneditable train/val/test chip when
                                    the project is locked. Hidden via
                                    SplitChip's nullable guard when split is
                                    not set (unlocked project). */}
                                <SplitChip split={img.split ?? null} isDarkMode={isDarkMode} size="xs" />
                                {/* Hover-revealed per-row actions only out of edit mode.
                                    In edit mode the row IS the action; trailing buttons
                                    would compete with the selection target. */}
                                {!editMode && (isHovered || active) && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDownloadImage(img);
                                            }}
                                            title={t('download_image', { defaultValue: 'Download image' })}
                                            className={cn(
                                                'p-1 rounded shrink-0 transition-colors',
                                                isDarkMode
                                                    ? 'hover:bg-primary-900/30 text-primary-400'
                                                    : 'hover:bg-primary-50 text-primary-600',
                                            )}
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteImage(img);
                                            }}
                                            title={t('delete_image', { defaultValue: 'Delete image' })}
                                            className={cn(
                                                'p-1 rounded shrink-0 transition-colors',
                                                isDarkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-500',
                                            )}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}
